/**
 * @file    envelope.ts
 * @scope   SHARED
 * @summary Reads the persisted envelope `{ chartTypeId, version, payload }` from a widget config.
 * @consumes CustomConfig
 * @produces ENVELOPE_KEYS, readEnvelope, writeEnvelope, isNewerThanKnown
 * @sideEffects `writeEnvelope` assigns two keys on the given config.
 * @notes   Keys are additive siblings of `QueryCollections`. Reading since Phase 2; writing since
 *          Phase 7 behind `useDiyEngineEnvelopeWrites` (see `writeEnvelope`). Missing/invalid
 *          `ChartTypeId` → `Chart` fallback → default subtype; missing/invalid `Version` → 1.
 */
import type { SerializationEnvelope } from '../contracts';

/** camelCase, aligned with the rest of `CustomConfig`. */
export const ENVELOPE_KEYS = {
  chartTypeId: 'chartTypeId',
  version: 'version',
  /** Backend chart discriminator (e.g. `"CrossProgramRollup"`); read-only fallback, see below. */
  chart: 'chart',
} as const;

const asNonEmptyString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.length > 0 ? value : undefined;

/**
 * Resolution order: `ChartTypeId` → `Chart` (only when it names a registered subtype) → default.
 * WHY `Chart`: subtypes like CPR already persist it as a required backend key, so they are
 * discriminated without waiting for envelope writes (Phase 7). Enterprise configs carry no
 * matching `Chart`, so they keep resolving to the default.
 *
 * @param config Persisted widget config (any shape).
 * @param defaultChartTypeId Subtype used for configs saved before the envelope existed.
 * @param knownChartTypeIds Registered subtype ids eligible for the `Chart` fallback.
 * @returns The resolved envelope; `payload` is the original config reference.
 */
export const readEnvelope = <TConfig>(
  config: TConfig,
  defaultChartTypeId: string,
  knownChartTypeIds: string[] = []
): SerializationEnvelope<TConfig> => {
  const raw = (config ?? {}) as Record<string, unknown>;
  const explicitId = asNonEmptyString(raw[ENVELOPE_KEYS.chartTypeId]);
  const chart = asNonEmptyString(raw[ENVELOPE_KEYS.chart]);
  const chartId = chart && knownChartTypeIds.includes(chart) ? chart : undefined;
  const version = raw[ENVELOPE_KEYS.version];

  return {
    chartTypeId: explicitId ?? chartId ?? defaultChartTypeId,
    version: typeof version === 'number' && Number.isInteger(version) && version > 0 ? version : 1,
    payload: config,
  };
};

/**
 * Phase 7: writes `ChartTypeId` + `Version` next to the existing payload (additive, payload untouched).
 *
 * TODO(ai): REMOVE the feature flag gate (`useDiyEngineEnvelopeWrites`, default off) once backend/
 * Guardrails confirm `CustomConfig` tolerates these extra keys and every deployed client reads the
 * envelope (plan §7.1). Until then callers must only invoke this when the flag is on.
 *
 * @param config Widget config to update; returned untouched when undefined.
 * @returns The same config reference.
 */
export const writeEnvelope = <TConfig extends object>(
  config: TConfig | undefined,
  chartTypeId: string,
  version: number
): TConfig | undefined => {
  if (!config) return config;
  const raw = config as Record<string, unknown>;
  raw[ENVELOPE_KEYS.chartTypeId] = chartTypeId;
  raw[ENVELOPE_KEYS.version] = version;
  return config;
};

/**
 * `true` when the config was written by a newer client than this one knows (→ read-only, §7.1).
 * @param knownVersion Payload version of the active subtype delta.
 */
export const isNewerThanKnown = (config: unknown, knownVersion: number): boolean =>
  readEnvelope(config, '').version > knownVersion;
